import { useState, useEffect, useCallback, act } from "react";
import { jwtDecode } from "jwt-decode";
import {
  getPatientById,
  updatePatient,
  getAccountByPatientId,
  updateAccount,
  bulkUpdatePatients,
  createAccount,
  removePatientFromAccount,
  addPatientToAccount,
  checkAccountByMedicalAid,
  removePatientsFromAccount,
  getAccountByAccountIdNumber,
  addPatientsToAccount,
} from "../../api";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import React from "react";
import Alert from "react-bootstrap/Alert";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import CheckEligibility from "./CheckEligibility";
import SearchMedAidPatient from "./SearchMedAidFamily";
import AccountSearch from "../SearchAccounts/SearchAccounts";
import "./PatientAccounts.css";
import { checkEligibility } from "../../mediKreditapi";

/**
 * Build just the patient‐update fields + the previous‐aid snapshot
 */
function movePatientBlock(selectedAccount, prevAid) {
  return {
    patientPreviousMedicalAid: prevAid,
    patientMedicalAid: selectedAccount?.accountMedicalAid,
    patientMedicalAidCode: selectedAccount?.accountMedicalAidCode,
    patientMedicalAidOption: selectedAccount?.accountMedicalAidOption,
    patientMedicalAidOptionCode: selectedAccount?.accountMedicalAidOptionCode,
    patientMedicalAidPlan: selectedAccount?.accountMedicalAidPlan,
    patientMedicalAidPlanCode: selectedAccount?.accountMedicalAidPlanCode,
    patientMedicalAidNumber: selectedAccount?.accountMedicalAidNumber,
    patientMedicalAidDependantCode: "99",
    patientMedicalAidJoinDate: new Date().toISOString().slice(0, 10),
    patientMedicalAidLastCheck: new Date(),
    patientMedicalAidStatus: selectedAccount?.dependantStatus || "",
    patientMediKreditPlanNumber03:
      selectedAccount?.accountMediKreditPlanNumber03 || "",
    patientMediKreditPlanNumber04:
      selectedAccount?.accountMediKreditPlanNumber04 || "",
    patientMediKreditPlanNumber06:
      selectedAccount?.accountMediKreditPlanNumber06 || "",
  };
}

function makePatientBlock(dep, pat, patient, prevAid) {
  const m = dep?.medicalAidInfo || {};
  const joined = dep?.dependantDateJoined
    ? new Date(dep?.dependantDateJoined).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);

  return {
    // $push this onto each patient
    patientPreviousMedicalAid: prevAid,
    patientIdNumber: dep?.dependantID || pat.patientIdNumber,
    patientFirstName: dep?.dependantFirstName || pat.patientFirstName,
    patientLastName: dep?.dependantLastName || pat.patientLastName,
    patientInitials: dep?.dependantInitials || pat.patientInitials,
    // patient medical-aid fields used in the loop
    patientMedicalAid: m?.medicalAid || dep?.medicalAid,
    patientMedicalAidCode: m?.medicalAidCode || dep?.medicalAidCode,
    patientMedicalAidOption: m?.medicalAidOption || dep?.medicalAidOption,
    patientMedicalAidOptionCode:
      m?.medicalAidOptionCode || dep?.medicalAidOptionCode,
    patientMedicalAidPlan: m?.medicalAidPlan || dep?.medicalAidPlan,
    patientMedicalAidPlanCode: m?.medicalAidPlanCode || dep?.medicalAidPlanCode,
    patientMedicalAidNumber: m?.ch_id || dep?.ch_id,
    patientMedicalAidDependantCode:
      dep?.dependantCode || pat?.patientMedicalAidDependantCode,
    patientMedicalAidJoinDate: joined,
    patientMedicalAidLastCheck: m?.lastCheck ?? new Date(),
    patientMedicalAidStatus: dep?.dependantStatus || "",
    patientMediKreditPlanNumber03:
      m?.mediKreditPlanNumber03 || dep?.mediKreditPlanNumber03,
    patientMediKreditPlanNumber04:
      m?.mediKreditPlanNumber04 || dep?.mediKreditPlanNumber04,
    patientMediKreditPlanNumber06:
      m?.mediKreditPlanNumber06 || dep?.mediKreditPlanNumber06,
  };
}

/**
 * Build just the account‐update fields
 */
function makeAccountBlock(dep, pat, account) {
  const m = dep?.medicalAidInfo || {};

  // ISO date for joinDate
  const joined = dep?.dependantDateJoined
    ? new Date(dep.dependantDateJoined).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);

  // helper to pick new or fallback-to-old
  const pick = (newVal, oldVal) =>
    pat.patientMainMember ? newVal : oldVal;

  return {
    // ─── ONLY THESE FIVE ARE CONDITIONAL ───
    accountFirstName: pick(
      dep?.dependantFirstName  || pat?.patientFirstName,
      account?.accountFirstName
    ),
    accountLastName: pick(
      dep?.dependantLastName   || pat?.patientLastName,
      account?.accountLastName
    ),
    accountInitials: pick(
      dep?.dependantInitials   || pat?.patientInitials,
      account?.accountInitials
    ),
    accountDOB: pick(
      dep?.dependantDOB        || pat?.patientDOB,
      account?.accountDOB
    ),
    accountIdNumber: pick(
      dep?.dependantID         || pat?.patientIdNumber,
      account?.accountIdNumber
    ),

    // ─── EVERYTHING ELSE ALWAYS UPDATES ───
    accountMedicalAid:         m.medicalAid           || dep?.medicalAid,
    accountMedicalAidCode:     m.medicalAidCode       || dep?.medicalAidCode,
    accountMedicalAidOption:   m.medicalAidOption     || dep?.medicalAidOption,
    accountMedicalAidOptionCode:
                                m.medicalAidOptionCode || dep?.medicalAidOptionCode,
    accountMedicalAidPlan:     m.medicalAidPlan       || dep?.medicalAidPlan,
    accountMedicalAidPlanCode: m.medicalAidPlanCode   || dep?.medicalAidPlanCode,
    accountMedicalAidNumber:   m.ch_id                || dep?.ch_id,

    status:   dep?.dependantStatus || "",
    joinDate: joined,

    MediKreditplanNumber03: m.mediKreditPlanNumber03 || dep?.mediKreditPlanNumber03,
    MediKreditplanNumber04: m.mediKreditPlanNumber04 || dep?.mediKreditPlanNumber04,
    MediKreditplanNumber06: m.mediKreditPlanNumber06 || dep?.mediKreditPlanNumber06,
  };
}


function newAccount(dep, pat) {
  const m = dep?.medicalAidInfo || {};
  const joined = dep?.dependantDateJoined
    ? new Date(dep?.dependantDateJoined).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  return {
    // used by the account‐update $set
    accountFirstName: pat?.patientFirstName,
    accountFullNames: pat?.patientFullNames,
    accountLastName: pat?.patientLastName,
    accountInitials: pat?.patientInitials,
    accountDOB: pat?.patientDOB,
    accountIdNumber: pat?.patientIdNumber,
    accountPatientId: pat?._id,
    accountTitle: pat?.patientTitle,
    accountTelNumberCell: pat?.patientCellNumber,
    accountEmail: pat?.patientEmail,
    accountPractice: pat?.patientPractice,
    accountCreatedBy: pat?.patientCreatedBy,
    accountCreatedDate: pat?.patientCreatedDate,
    accountMedicalAid: m.medicalAid || dep?.medicalAid,
    accountMedicalAidCode: m.medicalAidCode || dep?.medicalAidCode,
    accountMedicalAidOption: m.medicalAidOption || dep?.medicalAidOption,
    accountMedicalAidOptionCode:
      m.medicalAidOptionCode || dep?.medicalAidOptionCode,
    accountMedicalAidPlan: m.medicalAidPlan || dep?.medicalAidPlan,
    accountMedicalAidPlanCode: m.medicalAidPlanCode || dep?.medicalAidPlanCode,
    accountMedicalAidNumber: m.ch_id || dep?.ch_id,

    // these match the route’s use of medicalAidData.status / joinDate
    status: dep?.dependantStatus || "",
    joinDate: joined,

    // these must exist so medicalAidData.MediKreditplanNumber03 etc. line up
    MediKreditplanNumber03:
      m.mediKreditPlanNumber03 || dep?.mediKreditPlanNumber03,
    MediKreditplanNumber04:
      m.mediKreditPlanNumber04 || dep?.mediKreditPlanNumber04,
    MediKreditplanNumber06:
      m.mediKreditPlanNumber06 || dep?.mediKreditPlanNumber06,
  };
}

function makeBulkUpdatePayload(dep, pat, prevAid, account, dependantList) {
  return {
    ...makePatientBlock(dep, prevAid),
    ...makeAccountBlock(dep, pat, account),
    dependantList,
  };
}

function PatientEditModal({ patientUpdateId, show, onHide }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState();
  const [patientStatus, setPatientStatus] = useState("");
  const [userId, setUserId] = useState("");
  const [practice, setAPractice] = useState([]);
  const [closeAlert, setCloseAlert] = useState(false);
  const [dependantList, setDependantList] = useState([]); // State to store dependants list
  const [selectedDependant, setSelectedDependant] = useState(null); // State to store selected dependant
  const [importInfo, setImportInfo] = useState(false); // State to control the import modal
  const [prevAid, setPrevAid] = useState();
  const [notFoundAlert, setNotFoundAlert] = useState(false); // State to control the not found alert
  const [successMessage, setSuccessMessage] = useState(false); // State to control the success message
  const [mainMemberWarning, setMainMemberWarning] = useState(false); // State to control the main member warning
  const [removeMed, setRemoveMed] = useState(false); // State to control the remove action
  const [removeMedBulk, setRemoveMedBulk] = useState(false); // State to control the remove action for bulk update
  const [dependantListMainMember, setDependantListMainMember] = useState(); // State to store selected dependant main member
  const [newMainMemberWarning, setNewMainMemberWarning] = useState(false); // State to control the new main member warning
  const [noMainMemberAlert, setNoMainMemberAlert] = useState(false); // State to control the no main member alert
  const [blocked, setBlocked] = useState(false); // State to control the blocked state
  const [existingAccount, setExistingAccount] = useState(false); // State to control the existing account alert
  const [searchNewAccount, setSearchNewAccount] = useState(false); // State to control the search new account alert
  const [selectedAccount, setSelectedAccount] = useState(null); // State to store selected account
  const [updatedAccount, setUpdatedAccount] = useState(null); // State to store updated account
  const [notFoundMMAlert, setNotFoundMMAlert] = useState(false); // State to control the not found main member alert
  const [payload, setPayload] = useState({}); // State to store payload for bulk update
  const [noExistingAccountAlert, setNoExistingAccountAlert] = useState(false); // State to control the no existing account alert
  const [newDependantWarning, setNewDependantWarning] = useState(false); // State to control the new dependant warning
  const [isLoading, setIsLoading] = useState(false); // State to control loading state
  const [updated, setUpdated] = useState(false); // State to control if the patient has been updated
  const [moveToNewMainMemberAlert, setMoveToNewMainMemberAlert] =
    useState(false); // State to control the move to new main member alert
  // Patient Information State
  const [patient, setPatient] = useState({});
  const [eligibilityData, setEligibilityData] = useState(null);
  const [promoteToMainMember, setPromoteToMainMember] = useState(false); // State to control the promote to main member action
  const [condition, setCondition] = useState(""); // State to store the current condition for scenarios

  useEffect(() => {
    const fetch = async () => {
      const pid = patientUpdateId || id;
      if (!pid) return;

      const p = await getPatientById(pid);
      if (!p) return;

      setPatient(p);
      setPrevAid({
        patientMedicalAid: p?.patientMedicalAid,
        patientMedicalAidCode: p?.patientMedicalAidCode,
        patientMedicalAidOption: p?.patientMedicalAidOption,
        patientMedicalAidOptionCode: p?.patientMedicalAidOptionCode,
        patientMedicalAidPlan: p?.patientMedicalAidPlan,
        patientMedicalAidPlanCode: p?.patientMedicalAidPlanCode,
        patientMedicalAidNumber: p?.patientMedicalAidNumber,
        patientMedicalAidDependantCode: p?.patientMedicalAidDependantCode,
        patientMedicalAidStatus: p?.patientMedicalAidStatus,
        patientMedicalAidLastCheck: p?.patientMedicalAidLastCheck,
        patientMedicalAidJoinDate: p?.patientMedicalAidJoinDate,
        patientMediKreditPlanNumber03: p?.patientMediKreditPlanNumber03,
        patientMediKreditPlanNumber04: p?.patientMediKreditPlanNumber04,
        patientMediKreditPlanNumber06: p?.patientMediKreditPlanNumber06,
        patientMainMember: p?.patientMainMember,
      });
      console.log("Patient fetched:", p);
      if (p?.patientMedicalAid) {
        // (you had setMedicalAidMember(true) but never used it in your UI)
      }
      const acct = await getAccountByPatientId(p._id);
      console.log("Account fetched:", acct);
      if (acct) setAccount(acct);
    };
    fetch();
  }, [id, patientUpdateId]);

  const handleAccountSearchSelect = (accountSearch) => {
    if (accountSearch && accountSearch._id) {
      setSelectedAccount(accountSearch);
    }
  };

  // ─── “ADD PATIENT” SCENARIOS ─────────────────────────────────────────────────────
  //
  const scenarios = [
    // NEW1: Import Fund
    // 1) Dependant's main member not found during import only dependant updated with info
    {
      condition: ({ dep, pat }) =>
        (dep?.errorMessage && !pat.patientMainMember),
      execute: ({ dep, pat }) => {
        console.log("Condintion 1.1 met:");
        setCondition("1.1");
        setNotFoundAlert(true);
        console.log("dep being sent:", dep);
        setPatient((p) => ({
          ...p,
          ...makePatientBlock(dep, pat, prevAid),
          // override only dependantCode
          patientMedicalAidDependantCode: "99",
        }));
        console.log("Patient after condition 1:", patient);
      },
    },
    // 1.2) Dependant's main member not found during import only dependant updated with info
    {
      condition: ({ dep }) =>
        (dep === null && !promoteToMainMember && !newMainMemberWarning && !selectedAccount),
      execute: ({ pat }) => {
        console.log("Condintion 1.2 met:");
        setCondition("1.2");
        setPatient(pat);
      },
    },
    // 2) Main member not found during import, main member and all children updated with info
    {
      condition: ({ dep, pat }) => dep?.errorMessage && pat.patientMainMember,
      execute: ({ dep, pat, account }) => {
        console.log("Condintion 2 met:");
        setCondition("2");
        setPatient((p) => ({ ...p, ...makePatientBlock(dep, pat, prevAid) }));
        setAccount((a) => ({ ...a, ...makeAccountBlock(dep, pat, account) }));
        setPayload(makeBulkUpdatePayload(dep, pat, prevAid, account, dependantList));
      },
    },
    // 3) Main member found during import, main member and all children updated with info
    {
      condition: ({ dep, pat }) =>
        dep?.mainMember &&
        pat.patientMainMember &&
        dep.dependantID === pat.patientIdNumber,
      execute: ({ dep, pat, account }) => {
        console.log("Condintion 3 met:");
        setCondition("3");
        setMainMemberWarning(true);
        setPatient((p) => ({ ...p, ...makePatientBlock(dep, pat, prevAid) }));
        setAccount((a) => ({ ...a, ...makeAccountBlock(dep, pat, account) }));
        setPayload(makeBulkUpdatePayload(dep, pat, account, prevAid, dependantList));
      },
    },
    // 4.1) Dedpendant and it's main member found during import of fund details update
    {
      condition: ({ dep, pat }) =>
        !dep?.mainMember &&
        !pat.patientMainMember &&
        dep !== null,
      execute: async ({ dep, pat, account }) => {
        console.log("Condintion 4.1 met:");
        setCondition("4.1");
        setPatient((p) => ({ ...p, ...makePatientBlock(dep, pat, prevAid) }));
        setAccount((a) => ({ ...a, ...makeAccountBlock(dep, pat, account) }));
        setPayload(makeBulkUpdatePayload(dep, pat, account, prevAid, dependantList));
      },
    },
    // 4.2) Dependant not on Medical Aid, but not main member
    {
      condition: ({ dep, pat, promoteToMainMember, newMainMemberWarning }) =>
        !dep?.mainMember &&
        !pat.patientMainMember &&
        !pat.patientMedicalAid &&
        !promoteToMainMember &&
        !newMainMemberWarning &&
        !selectedAccount,
      execute: async ({ pat }) => {
        console.log("Condintion 4.2 met:");
        setCondition("4.2");
        setUpdated(true);
        setPatient((p) => ({
          ...p,
          pat,
        }));
      },
    },
    // 4.3) Main Member not on Medical Aid, update main member account and patient account
    {
      condition: ({
        pat,
        promoteToMainMember,
        newMainMemberWarning,
        importInfo,
      }) =>
        pat.patientMainMember &&
        !pat.patientMedicalAid &&
        !promoteToMainMember &&
        !newMainMemberWarning &&
        !importInfo &&
        !selectedAccount,
      execute: async ({ dep, pat }) => {
        console.log("Condintion 4.3 met:");
        setCondition("4.3");
        setUpdated(true);
        setPatient((p) => ({
          ...p,
          pat,
        }));
        setUpdatedAccount({
          ...makeAccountBlock(dep, pat, account),
          accountTitle: pat?.patientTitle,
          accountInitials: pat?.patientInitials,
          accountFullNames: pat?.patientFullNames,
          accountLastName: pat?.patientLastName,
          accountFirstName: pat?.patientFirstName,
          accountTelNumberCell: pat?.patientCellNumber,
          accountEmail: pat?.patientEmail,
          accountPatientId: pat?._id,
          accountPractice: pat?.patientPractice,
        });
      },
    },

    // 5) Move the dependant to a new main member account Med Aid - if the account is found and it mis matches
    //    the information of the patient, the account and it's dependants will be updated
    {
      condition: ({ dep, pat }) =>
        !dep?.mainMember &&
        !pat?.patientMainMember &&
        dep?.medicalAidInfo?.ch_id !== pat.patientMedicalAidNumber &&
        !selectedAccount && dep?.medicalAidInfo?.ch_id,
      execute: async ({ dep, pat, account }) => {
        console.log("Condintion 5 met:");
        console.log("dep", dep);
        setCondition("5.1");
        const resp = await checkAccountByMedicalAid(dep?.medicalAidInfo?.ch_id);
        if (resp) {
          setUpdatedAccount(resp);
          setMoveToNewMainMemberAlert(true);
          setPatient((p) => ({
            ...p,
            ...makePatientBlock(dep, pat, prevAid),
          }));
        } else {
          const a = await getAccountByPatientId(pat._id);
          console.log("Existing Account33:", a);
          if (
            !a.accountMedicalAid &&
            a.accountLastName === dependantList[0].dependantLastName &&
            a.accountIdNumber === dependantList[0].dependantID
          ) {
            setCondition("5.2");
            setUpdatedAccount(a);
            setPatient((p) => ({
              ...p,
              ...makePatientBlock(dep, pat, prevAid),
            }));
            setPayload(makeBulkUpdatePayload(dep, account, prevAid, dependantList));
          } else {
            setNoExistingAccountAlert(true);
          }
        }
      },
    },

    // 6) The medical aid dependant becomes the main member of their own medical aid account
    {
      condition: ({ dep, pat }) =>
        dep?.mainMember &&
        !pat?.patientMainMember &&
        dep?.dependantID === pat.patientIdNumber,
      execute: ({ dep, pat }) => {
        console.log("Condintion 6 met:");
        setCondition("6");
        setNewMainMemberWarning(true);
        setPatient((p) => ({
          ...p,
          ...makePatientBlock(dep, pat, prevAid),
          patientMainMember: true,
        }));
        setUpdatedAccount({
          ...newAccount(dep, pat)
        });
      },
    },

    // 7.1) Private dependant becomes dependant on an existing account
    {
      condition: ({ account, selectedAccount, dep, pat }) =>
        !pat?.patientMainMember && selectedAccount && dep === null,
      execute: async ({ selectedAccount, dep, pat, prevAid }) => {
        console.log("Condition 7.1 met");
        setCondition("7.1");
        setMoveToNewMainMemberAlert(true);
        setUpdatedAccount(selectedAccount);
        setPatient((p) => ({
          ...p,
          ...movePatientBlock(selectedAccount, prevAid),
          patientMainMember: false,
          patientMedicalAidDependantCode: "99",
        }));
      },
    },

    // 7.2) Private dependant becomes dependant on an existing account
    {
      condition: ({ account, selectedAccount, dep, pat }) =>
        !pat?.patientMainMember && selectedAccount && !dep?.mainMember && dep !== null,
      execute: async ({ selectedAccount, dep, pat, prevAid }) => {
        console.log("Condition 7.2 met");
        setCondition("7.2");
        setMoveToNewMainMemberAlert(true);
        setUpdatedAccount(selectedAccount);
        setPatient((p) => ({
          ...p,
          ...movePatientBlock(selectedAccount, prevAid),
          patientMainMember: false,
        }));
      },
    },

    // 8.1) Medical Aid main member becomes a dependant on an existing med account
    {
      condition: ({ dep, pat }) =>
        !dep?.mainMember &&
        pat.patientMainMember &&
        dep?.dependantID === pat.patientIdNumber,
      execute: async ({ pat, dep }) => {
        console.log("Condintion 8.1 met:");
        setCondition("8.1");
        const mm = await checkAccountByMedicalAid(dep?.medicalAidInfo?.ch_id);
        if (mm) {
          setNewDependantWarning(true);
          setUpdatedAccount(mm);
          setPatient((p) => ({
            ...p,
            ...movePatientBlock(pat, dep, prevAid),
            patientMainMember: false,
            patientMedicalAidDependantCode: "99",
          }));
        } else {
          setNoExistingAccountAlert(true);
        }
      },
    },

    // 8.2) Medical Aid main member becomes a dependant on an existing priv account
    {
      condition: ({ selectedAccount, account, pat }) =>
        pat.patientMainMember && selectedAccount && !account.MedicalAid,
      execute: async ({ pat, dep }) => {
        console.log("Condintion 8.2 met:");
        setCondition("8.2");
        setNewDependantWarning(true);
        setUpdatedAccount(selectedAccount);
        setPatient((p) => ({
          ...p,
          ...movePatientBlock(pat, dep, prevAid),
          patientMainMember: false,
          patientMedicalAidDependantCode: "99",
        }));
      },
    },

    // 9) Move private main member AND dependants to new account
    {
      condition: ({ selectedAccount, pat }) =>
        pat?.patientMainMember && selectedAccount && !pat.patientMedicalAid,
      execute: async ({ selectedAccount, dep, pat, prevAid }) => {
        console.log("Condition 9 met");
        setCondition("9");
        setNewDependantWarning(true);
        setUpdatedAccount(selectedAccount);
        setPatient((p) => ({
          ...p,
          ...movePatientBlock(pat, dep, prevAid),
          patientMainMember: false,
          patientMedicalAidDependantCode: "99",
        }));
      },
    },
  ];

  useEffect(() => {
    // if you have a private patient (dep===null) *and* now have a selected account,
    // re-run your scenario engine.
    if (selectedAccount && selectedDependant == null) {
      handleAddPatientSteps(null);
    }
    if (updated) handleAddPatientSteps(patient);
    // Reset the updated state after handling the steps
    setUpdated(false);
  }, [selectedAccount, updated]);

  const handleAddPatientSteps = useCallback(
    async depOrPatient => {
    // if it’s literally the patient object, we’re in “update” mode
    const isUpdateCall = depOrPatient === patient

    // only treat it as a dependant if we’re _not_ in update mode
    const actualDependant = isUpdateCall
      ? null
      : depOrPatient

      console.log("dep", actualDependant);
      console.log("dep list", dependantList);
      console.log("account", account);
      console.log("selectedAccount", selectedAccount);
      console.log("patient", patient);
      console.log("prevAid", prevAid);

      setSelectedDependant(actualDependant);
      if (actualDependant?.errorMessage) {
        setNotFoundAlert(true);
        setSelectedDependant();
      }
      const ctx = {
        dep: actualDependant,
        pat: patient,
        account,
        selectedAccount,
        prevAid,
        dependantList,
        importInfo,
        moveToNewMainMemberAlert,
        promoteToMainMember,
        updated,
      };
      let counter = 0;
      console.log("Running scenarios with context:", ctx);
      for (let { condition, execute } of scenarios) {
        counter++;
        console.log("Counter:", counter);
        if (condition(ctx)) {
          await execute(ctx);
          break;
        }
      }
    },
    // now include account and selectedAccount
    [
      patient,
      account,
      selectedAccount,
      prevAid,
      dependantList,
      moveToNewMainMemberAlert,
      promoteToMainMember,
      updated,
    ]
  );

  // Handling the change of the input fields
  async function handleChangePatient(e) {
    const { name, checked } = e.target;
    const token = sessionStorage.getItem("user");
    const decoded = jwtDecode(token);
    setUserId(decoded._id);
    const userPractice = decoded.practice;
    setAPractice(userPractice);
    setPatient((prevPatient) => ({
      ...prevPatient,
      [e.target.name]: e.target.value,
      patientCreatedBy: userId,
      patientStatus: patientStatus,
    }));
    setUpdated(true);
  }

  const handleRemove = () => {
    setPatient((prev) => ({
      ...prev,
      patientMedicalAid: "",
      patientMedicalAidCode: "",
      patientMedicalAidOption: "",
      patientMedicalAidOptionCode: "",
      patientMedicalAidPlan: "",
      patientMedicalAidPlanCode: "",
      patientMedicalAidNumber: "",
      patientMedicalAidDependantCode: "",
      patientMedicalAidLastCheck: "",
      patientMedicalAidStatus: "",
      patientMedicalAidJoinDate: "",
      patientMediKreditPlanNumber03: "",
      patientMediKreditPlanNumber04: "",
      patientMediKreditPlanNumber06: "",
      patientPreviousMedicalAid: prevAid,
    }));
    setAccount((prev) => ({
      ...prev,
      accountMedicalAid: "",
      accountMedicalAidCode: "",
      accountMedicalAidOption: "",
      accountMedicalAidOptionCode: "",
      accountMedicalAidPlan: "",
      accountMedicalAidPlanCode: "",
      accountMedicalAidNumber: "",
      accountMedicalAidStatus: "",
      accountMedicalAidJoinDate: "",
      accountMediKreditplanNumber03: "",
      accountMediKreditplanNumber04: "",
      accountMediKreditplanNumber06: "",
    }));
    if (patient.patientMainMember) {
      setMainMemberWarning(true); // Trigger UI warning
      setRemoveMedBulk(true);
      setPayload({
        patientMedicalAid: "",
        patientMedicalAidCode: "",
        patientMedicalAidOption: "",
        patientMedicalAidOptionCode: "",
        patientMedicalAidPlan: "",
        patientMedicalAidPlanCode: "",
        patientMedicalAidNumber: "",
        patientMedicalAidStatus: "",
        patientMedicalAidLastCheck: "",
        patientMedicalAidJoinDate: "",
        patientMediKreditPlanNumber03: "",
        patientMediKreditPlanNumber04: "",
        patientMediKreditPlanNumber06: "",
        patientPreviousMedicalAid: prevAid,
        dependantFistName: patient?.patientFirstName,
        dependantLastName: patient?.patientLastName,
        dependantInitials: patient?.patientInitials,
        dependantDOB: patient?.patientDOB,
        dependantID: patient?.patientIdNumber,
        accountMedicalAid: "",
        accountMedicalAidCode: "",
        accountMedicalAidOption: "",
        accountMedicalAidOptionCode: "",
        accountMedicalAidPlan: "",
        accountMedicalAidPlanCode: "",
        accountMedicalAidNumber: "",
        accountMedicalAidstatus: "",
        accountMedicalAidjoinDate: "",
        accountMediKreditplanNumber03: "",
        accountMediKreditplanNumber04: "",
        accountMediKreditplanNumber06: "",
      });
    } else {
      setRemoveMed(true); // Trigger UI warning
    }
  };

  const handleUpdateDependantList = (list) => {
    setDependantList(list);
    setDependantListMainMember(list.filter((item) => item?.mainMember));
  };

  // ─── “SAVE” SCENARIOS ────────────────────────────────────────────────────────────
  //
  const saveScenarios = [
    // 1) bulk‐update main + dependants
    {
      condition: () => condition === "1.1" || condition === "1.2" || condition === "4.2",
      execute: async ({ pat }) => {
        console.log("Save Condintion 1 & 4.2:");
        await updatePatient(pat._id, pat);
      },
    },
    {
      condition: () =>
        condition === "2" ||
        condition === "3" ||
        condition === "5.2" ||
        condition === "4.1",
      execute: async ({ account, pat, payload, dependantList }) => {
        console.log("Save Condintion 2 of 3 of 5.2 of 4.1");
        //await updateAccount(account._id, account);
        await updatePatient(pat._id, pat);
        const ids = Array.isArray(account.accountPatientId)
          ? account.accountPatientId
          : [];
        await bulkUpdatePatients(ids, account._id, payload, dependantList);
      },
    },
    {
      condition: () => condition === "4.3",
      execute: async ({ pat, updatedAccount, prevAid }) => {
        console.log("Save Condintion 4.3");
        const response = await getAccountByPatientId(pat._id);
        await updateAccount(response._id, updatedAccount);
        await updatePatient(pat._id, pat, prevAid);
      },
    },

    {
      // 3) existing‐patient on a new or existing account
      condition: () => condition === "5.1",
      execute: async ({ pat, updatedAccount, account }) => {
        console.log("Save Condintion 5.1:");

        await updatePatient(pat._id, pat);
        await addPatientToAccount(updatedAccount._id, pat._id);
        await removePatientFromAccount(account._id, pat._id);
      },
    },

    // 4) demote main ⇒ bulk‐update under new main
    {
      condition: () => (condition === "7.1" || condition === "7.2"),
      execute: async ({ account, pat }) => {
        console.log("Save Condintion 7");
        await removePatientFromAccount(account._id, pat._id);
        await addPatientToAccount(selectedAccount._id, pat._id);
      },
    },
    {
      condition: () =>
        condition === "8.1" || condition === "8.2" || condition === "9",
      execute: async ({ account, selectedAccount }) => {
        console.log("Save Condintion 8.1, 8.2, 9");
        const ids = Array.isArray(account.accountPatientId)
          ? account.accountPatientId
          : [];
        await removePatientsFromAccount(account._id, ids);
        await addPatientsToAccount(selectedAccount._id, ids);
      },
    },

    // 5) promote a dependant into brand‐new account
    {
      condition: () => condition === "6",
      execute: async ({ account, updatedAccount, pat }) => {
        console.log("Save Condintion 6 met:");
        await removePatientFromAccount(account._id, pat._id);
        await createAccount(updatedAccount);
        await updatePatient(pat._id, pat);
      },
    },

    {
      condition: () => condition === "7",
      execute: async ({ account, selectedAccount, pat }) => {
        console.log("Save Condintion 7 met:");

        console.log("account._id:", account._id);
        console.log("selectedAccount._id:", selectedAccount._id);
        console.log("pat._id:", pat._id);
        await removePatientFromAccount(account._id, pat._id);
        await updatePatient(pat._id, pat);
        await addPatientToAccount(selectedAccount._id, pat._id);
      },
    },

    // 6) remove medical aid from all in account
    {
      condition: ({ removeMedBulk }) => removeMedBulk,
      execute: async ({ account, payload, dependantList }) => {
        console.log("Save Condintion 10 met:");
        await bulkUpdatePatients(
          account.accountPatientId,
          account._id,
          payload,
          dependantList
        );
      },
    },

    // 7) remove medical aid from single patient
    {
      condition: ({ removeMed }) => removeMed,
      execute: async ({ pat, account }) => {
        console.log("Save Condintion 11 met:");
        await updatePatient(pat._id, pat);
        await updateAccount(account._id, account);
      },
    },
  ];

  async function handleSave() {
    setIsLoading(true);
    try {
      //   const cresponse = await updatePatient(patient._id, patient);
      const ctx = {
        sel: selectedDependant,
        dep: selectedDependant,
        pat: patient,
        account,
        updatedAccount,
        selectedAccount,
        prevAid,
        payload,
        dependantList,
        moveToNewMainMemberAlert,
        removeMed,
        removeMedBulk,
        promoteToMainMember,
      };
      let counter = 0;
      console.log("Running save scenarios with context:", ctx);

      for (let { condition, execute } of saveScenarios) {
        console.log("Checking condition:", condition(ctx));
        counter++;
        console.log("Counter:", counter);
        if (condition(ctx)) {
          await execute(ctx);
          break;
        }
      }

      setSuccessMessage(true);
      setTimeout(() => {
        onHide();
        setSuccessMessage(false);
        navigate(0);
      }, 2000);
    } catch (err) {
      console.error(err);
      alert("Error saving patient.");
    } finally {
      setIsLoading(false);
      setRemoveMed(false);
      setRemoveMedBulk(false);
      setNotFoundAlert(false);
      setNotFoundMMAlert(false);
      setMainMemberWarning(false);
      setNewMainMemberWarning(false);
      setNoExistingAccountAlert(false);
      
      setPatient(null); // Reset patient state
    setExistingAccount(false); // Reset existing account alert state  
    setAccount(null); // Reset account state
    setDependantList(null); // Reset dependant list state
    setImportInfo(false); // Reset import info state
    setSearchNewAccount(false); // Reset search new account state
    setUpdatedAccount(null); // Reset updated account state
setSelectedAccount(null); // Reset selected account state
    setSelectedDependant(null); // Reset selected dependant state
    }
    
  }

  function handleClose() {
    setCloseAlert(true); // Show confirmation alert
  }

  const handleCancelNo = () => {
    setCloseAlert(false); // Hide alert and keep modal open
  };

  const handleCancelYes = async () => {
    setCloseAlert(false);
    navigate(0);
    setCloseAlert(false); // Close the modal
    setSuccessMessage(false); // Reset success message state
    setNotFoundAlert(false); // Reset not found alert state
    setNotFoundMMAlert(false); // Reset not found main member alert state
    setMainMemberWarning(false); // Reset main member warning state
    setRemoveMed(false); // Reset remove action state
    setRemoveMedBulk(false); // Reset remove action for bulk update state
    setNewMainMemberWarning(false); // Reset new main member warning state
    setNoMainMemberAlert(false); // Reset no main member alert state
    setBlocked(false); // Reset blocked state
    setPatient(null); // Reset patient state
    setExistingAccount(false); // Reset existing account alert state  
    setAccount(null); // Reset account state
    setDependantList(null); // Reset dependant list state
    setImportInfo(false); // Reset import info state
    setSearchNewAccount(false); // Reset search new account state
    setUpdatedAccount(null); // Reset updated account state
setSelectedAccount(null); // Reset selected account state
    setSelectedDependant(null); // Reset selected dependant state
    // Close the modal after successful operation
  };

  const handleCheckStatus = () => {
    // Implement the logic for checking status
  };

  // Rendering the content of the forms based on the current step
  const renderContent = () => {
    return (
      <Form>
        {searchNewAccount && !selectedAccount && (
          <Row className="mb-1">
            <p className="text-primary">
              NOTE: If you can't find the account please create the account
              patient with it's account first via the add patient function.
            </p>
          </Row>
        )}
        {selectedAccount && searchNewAccount && !importInfo && (
          <>
            <Row className="mb-1">
              <strong className="text-primary">Selected Account:</strong>
            </Row>
            <Row className="mb-0">
              <Col md={4}>
                <Form.Label className="fw-normal">
                  {selectedAccount.accountTitle}{" "}
                  {selectedAccount.accountFullNames}{" "}
                  {selectedAccount.accountLastName} (
                  {selectedAccount.accountFirstName})
                </Form.Label>
              </Col>
              <Col md={6}></Col>
            </Row>

            {/* Three Equal Columns */}
            <Row>
              {/* Column 1: Personal Details */}
              <Col md={4}>
                {selectedAccount.accountIdNumber && (
                  <>
                    <i className="fa-solid fa-id-card me-1 text-primary"></i>{" "}
                    {selectedAccount.accountIdNumber} <br />
                  </>
                )}
                {selectedAccount.accountCellNumber && (
                  <>
                    <i className="fas fa-phone-alt me-2 text-primary"></i>{" "}
                    {selectedAccount.accountCellNumber} <br />
                  </>
                )}
                {selectedAccount.accountEmail && (
                  <>
                    <i className="fas fa-envelope me-2 text-primary"></i>{" "}
                    {selectedAccount.accountEmail} <br />
                  </>
                )}
              </Col>
              <Col md={6}>
                {selectedAccount.accountMedicalAid ? (
                  <>
                    <i className="fa fa-notes-medical me-2 text-primary"></i>{" "}
                    {selectedAccount.accountMedicalAid} <br />
                    <i className="fa fa-notes-medical me-2 text-white"></i>{" "}
                    {selectedAccount.accountMedicalAidPlan} |{" "}
                    {selectedAccount.accountMedicalAidOption} <br />
                    <i className="fa fa-notes-medical me-2 text-white"></i>{" "}
                    {selectedAccount.accountMedicalAidNumber} <br />
                  </>
                ) : (
                  <p className="text-primary">
                    <i className="fa fa-notes-medical me-2 text-primary"></i>{" "}
                    This is a Private account
                  </p>
                )}
              </Col>
            </Row>
            <hr />
          </>
        )}
        <Row className="mb-3 mt-3">
          <Col md={3}>
            <Form.Group as={Col} controlId="formGridPatientnumber">
              <Form.Label>Patient Number</Form.Label>
              <Form.Control
                required
                className="w-75"
                placeholder="Custom Patient Number"
                name="patientNumber"
                value={patient?.patientNumber}
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            {(selectedDependant?.medicalAidInfo?.errorMessage ||
              selectedAccount?.accountMedicalAid) && (
              <Form.Group as={Col} controlId="formGridDependantCode">
                <Form.Label>Dependant Code:</Form.Label>
                <Form.Control
                  required
                  className="w-75"
                  placeholder="Dependant Code"
                  name="patientMedicalAidDependantCode"
                  value={patient?.patientMedicalAidDependantCode}
                  onChange={handleChangePatient}
                />
              </Form.Group>
            )}
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={2}>
            <Form.Group as={Col} controlId="formGridTitle">
              <Form.Label>Title</Form.Label>
              <Form.Select
                required
                defaultValue=""
                name="patientTitle"
                value={patient?.patientTitle}
                onChange={handleChangePatient}
              >
                <option></option>
                <option>Ms</option>
                <option>Mr</option>
                <option>Mrs</option>
                <option>Dr</option>
                <option>Prof</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group as={Col} controlId="formGridFullNames">
              <Form.Label>Full Name(s)</Form.Label>
              <Form.Control
                required
                name="patientFullNames"
                value={patient?.patientFullNames}
                readOnly={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                }
                className={
                  patient?.patientDataSource === "familyCheck" &&
                  !selectedDependant?.medicalAidInfo?.errorMessage
                    ? "bg-light"
                    : ""
                }
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group as={Col} controlId="formGridLastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                required
                name="patientLastName"
                value={patient?.patientLastName}
                readOnly={
                  patient?.patientDataSource &&
                  !selectedDependant?.medicalAidInfo?.errorMessage ===
                    "familyCheck"
                }
                className={
                  patient?.patientDataSource &&
                  !selectedDependant?.medicalAidInfo?.errorMessage ===
                    "familyCheck"
                    ? "bg-light"
                    : ""
                }
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group as={Col} controlId="formGridInitials">
              <Form.Label>Initials</Form.Label>
              <Form.Control
                name="patientInitials"
                value={patient?.patientInitials}
                readOnly={
                  patient?.patientDataSource &&
                  !selectedDependant?.medicalAidInfo?.errorMessage ===
                    "familyCheck"
                }
                className={
                  patient?.patientDataSource &&
                  !selectedDependant?.medicalAidInfo?.errorMessage ===
                    "familyCheck"
                    ? "bg-light"
                    : ""
                }
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group as={Col} controlId="formGridFirstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                required
                value={patient?.patientFirstName}
                readOnly={
                  patient?.patientDataSource &&
                  !selectedDependant?.medicalAidInfo?.errorMessage ===
                    "familyCheck"
                }
                className={
                  patient?.patientDataSource &&
                  !selectedDependant?.medicalAidInfo?.errorMessage ===
                    "familyCheck"
                    ? "bg-light"
                    : ""
                }
                name="patientFirstName"
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group as={Col} controlId="formGridDOB">
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                required
                type="date"
                value={patient?.patientDOB}
                readOnly={
                  patient?.patientDataSource &&
                  selectedDependant?.medicalAidInfo?.errorMessage !==
                    "familyCheck"
                }
                className={
                  patient?.patientDataSource &&
                  selectedDependant?.medicalAidInfo?.errorMessage !==
                    "familyCheck"
                    ? "bg-light"
                    : ""
                }
                name="patientDOB"
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group as={Col} controlId="formGridLastName">
              <Form.Label>ID Number</Form.Label>
              <Form.Control
                required
                name="patientIdNumber"
                value={patient?.patientIdNumber}
                readOnly={
                  patient?.patientDataSource &&
                  !selectedDependant?.medicalAidInfo?.errorMessage ===
                    "familyCheck"
                }
                className={
                  patient?.patientDataSource &&
                  !selectedDependant?.medicalAidInfo?.errorMessage ===
                    "familyCheck"
                    ? "bg-light"
                    : ""
                }
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group as={Col} controlId="formGridCellPhone">
              <Form.Label>Mobile Number</Form.Label>
              <Form.Control
                required
                value={patient?.patientCellNumber}
                name="patientCellNumber"
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group as={Col} controlId="formGridEmailAddress">
              <Form.Label>Email</Form.Label>
              <Form.Control
                required
                value={patient?.patientEmail}
                name="patientEmail"
                onChange={handleChangePatient}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group as={Col} controlId="formGridTitle">
              <Form.Label>Gender</Form.Label>
              <Form.Select
                required
                value={patient?.patientGender}
                readOnly={
                  patient?.patientDataSource &&
                  selectedDependant?.medicalAidInfo?.errorMessage !==
                    "familyCheck"
                }
                disabled={
                  patient?.patientDataSource &&
                  selectedDependant?.medicalAidInfo?.errorMessage !==
                    "familyCheck"
                }
                className={
                  patient?.patientDataSource === "familyCheck" ? "bg-light" : ""
                }
                name="patientGender"
                onChange={(e) =>
                  setPatient((prev) => ({
                    ...prev,
                    patientGender: e.target.value,
                  }))
                }
              >
                <option value="">Select Gender</option>
                <option value="2">Female</option>
                <option value="1">Male</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3"></Row>
      </Form>
    );
  };

  return (
    <>
      <Modal
        show={show}
        //size="xl"
        centered
        aria-labelledby="contained-modal-title-vcenter"
        onHide={handleClose}
        dialogClassName="modal-80w"
      >
        <Modal.Header closeButton={!closeAlert} className="border-secondary">
          <div className="w-100">
            <Modal.Title
              id="contained-modal-title-vcenter"
              className="text-primary fs-5"
            >
              Update Patient Information
              <Row className="fw-normal fs-6 fw-light">
                <Form.Text>
                  Please enter the patient information in the form below.
                </Form.Text>
              </Row>
            </Modal.Title>
          </div>
        </Modal.Header>

        {/* Move the Alert outside Modal.Title but keep it inside Modal.Header */}
        {closeAlert && (
          <Alert
            variant="info"
            className="w-100 d-flex align-items-center justify-content-between fw-normal fs-6 mx-0 mb-0 border-0 rounded-0"
            onClose={() => setCloseAlert(false)}
          >
            <span>
              Are you sure you want to close? The updates will not be saved.
            </span>
            <div className="ms-auto d-flex">
              <Button
                variant="outline-primary"
                className="btn btn-outline-primary me-1"
                onClick={handleCancelYes}
              >
                <FaCheck />
              </Button>
              <Button
                variant="outline-secondary"
                className="btn btn-outline-secondary me-1"
                onClick={handleCancelNo}
              >
                <FaTimes />
              </Button>
            </div>
          </Alert>
        )}
        {notFoundAlert && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setNotFoundAlert(false)}
          >
            <span>
              The main member's online medical aid record couldn't be retrieved,
              this could be due to it either not being available from the fund
              or a connection issue. Please ensure to enter the the patient's{" "}
              <strong>dependant code</strong>. The Medical Aid information
              searched on will be saved against the patient record.
            </span>
          </Alert>
        )}
        {notFoundMMAlert && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setNotFoundMMAlert(false)}
          >
            <span>
              This main member's online medical aid record couldn't be
              retrieved, this could be due to it either not being available from
              the fund or a connection issue. Any changes made on this patient
              will be applied to all it's dependants.
            </span>
          </Alert>
        )}
        {noExistingAccountAlert && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setNoExistingAccountAlert(false)}
          >
            <span>
              The main member for the selected dependant was not found in
              PractoSync. Please first create the main member's patient and
              Account before attempting to move the dependant to their account.
            </span>
          </Alert>
        )}
        {selectedAccount?.accountMedicalAid && (
          <Alert
            variant="info"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            dismissible
          >
            <span>
              The selected account is currently on Medical Aid, if this is still
              correct consider importing the dependant via the Import Fund
              function.
            </span>
          </Alert>
        )}
        {moveToNewMainMemberAlert && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal mt-0"
            onClose={() => setMoveToNewMainMemberAlert(false)}
          >
            <span>
              This patient will be moved to a new the new account and inherit
              the Medical Aid information.
            </span>
          </Alert>
        )}
        {mainMemberWarning && !removeMedBulk && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setMainMemberWarning(false)}
          >
            <span>
              You are updating the main member's information. The changes to
              Medical Aid information will be applied to all dependants.
            </span>
          </Alert>
        )}
        {newMainMemberWarning && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setNewMainMemberWarning(false)}
          >
            <span>
              The selected dependant was not registered as a main member before.
              They will now be the main member on their own account. Any
              outstanding invoices will not be transferred to the new account.
            </span>
          </Alert>
        )}
        {newDependantWarning && (
          <Alert
            variant="warning"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setNewMainMemberWarning(false)}
          >
            <span>
              The selected dependant was a main member before. The dependant
              will be moved to the new account with all their existing
              dependants. Any outstanding invoices will not be transferred to
              the new account.
            </span>
          </Alert>
        )}
        {removeMed && (
          <Alert
            variant="danger"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => removeMed(false)}
          >
            <span>
              NOTE: You are about to remove the medical aid information from
              this patient. If you submit all claims still in process with the
              medical aid will remain with the medical aid until you reverse
              them.
            </span>
          </Alert>
        )}
        {removeMedBulk && (
          <Alert
            variant="danger"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => removeMedBulk(false)}
          >
            <span>
              NOTE: You are about to remove the medical aid information from
              this account. The medical aid information will also be removed
              from all it's dependants. If you submit all claims still in
              process with the medical aid will remain with the medical aid
              until you reverse them.
            </span>
          </Alert>
        )}
        {successMessage && (
          <Alert
            variant="success"
            className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            onClose={() => setSuccessMessage(false)}
          >
            <span>
              The patient's information has been successfully updated.
            </span>
          </Alert>
        )}

        <Modal.Body>
          <Row className="g-3">
            {/* Left column: always‑visible buttons */}
            <Col
              md={3}
              className="sidebar d-flex flex-column border-end border-1 pe-3"
            >
              <Button
                variant="primary"
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={() => setImportInfo(true)}
              >
                <i className="fa-solid fa-file-import me-2 ms-2" />
                <span className="btn-text">Import Fund</span>
              </Button>

              <Button
                variant="primary"
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={handleRemove}
              >
                <i className="fa-solid fa-trash me-2 ms-2" />
                <span className="btn-text">Remove Medical Aid</span>
              </Button>

              <Button
                variant="primary"
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={() => {
                  setPromoteToMainMember(true);
                  handleAddPatientSteps(patient);
                }}
              >
                <i className="fa-solid fa-user-check me-2 ms-2" />
                <span className="btn-text">Promote to Main Member</span>
              </Button>

              <Button
                variant="primary"
                className="w-100 mb-2 d-flex align-items-center justify-content-start gap-2"
                onClick={() => setSearchNewAccount(true)}
              >
                <i className="fa-solid fa-arrow-right-to-bracket me-2 ms-2" />
                <span className="btn-text">Move to New Account</span>
              </Button>
            </Col>

            {/* Right column: patient info + conditional content */}
            <Col md={9} className="info-col">
              {/* Patient header info */}
              <Row className="mb-3">
                <Col md={4}>
                  <strong>
                    {patient?.patientTitle} {patient?.patientInitials}{" "}
                    {patient?.patientFirstName} {patient?.patientLastName}
                  </strong>
                </Col>

                <Col md={4}>
                  {patient?.patientMedicalAid && (
                    <>
                      <p className="mb-1">{patient?.patientMedicalAid}</p>
                      <p className="mb-1">
                        {patient?.patientMedicalAidPlan} |{" "}
                        {patient?.patientMedicalAidOption}
                      </p>
                      <p className="mb-1">
                        {patient?.patientMedicalAidNumber} (
                        {patient?.patientMedicalAidDependantCode})
                      </p>
                    </>
                  )}
                </Col>

                <Col md={4}>
                  {patient?.patientMedicalAidStatus && (
                    <>
                      <p
                        className={
                          patient?.patientMedicalAidStatus
                            .toLowerCase()
                            .includes("active")
                            ? "text-success"
                            : "text-danger"
                        }
                      >
                        {patient?.patientMedicalAidStatus}
                        {patient?.patientMedicalAidJoinDate && (
                          <span className="fw-normal text-muted ms-2">
                            (Joined: {patient?.patientMedicalAidJoinDate})
                          </span>
                        )}
                      </p>
                      <p className="mb-2 fs-sm">
  Last Checked:{" "}
  {(() => {
    const raw = patient?.patientMedicalAidLastCheck;
    if (!raw || !raw.trim()) return "Not yet checked";

    const d = new Date(raw);
    if (isNaN(d)) return "Invalid date";

    return d.toISOString().replace("T", " ").slice(0, 16);
  })()}
</p>

                      <CheckEligibility
                        patient={patient}
                        account={account}
                        className="w-100"
                        onEligibility={(data) => setEligibilityData(data)}
                      />
                    </>
                  )}
                </Col>
              </Row>

              {/* Import fund flow */}
              {importInfo && (
                <Row className="mb-3">
                  <Col md={12}>
                    <SearchMedAidPatient
                      onDependantListUpdate={handleUpdateDependantList}
                      addPatientSteps={handleAddPatientSteps}
                    />
                  </Col>
                </Row>
              )}

              {/* Move‑to‑account flow */}
              {searchNewAccount && !importInfo && (
                <Row className="mb-3">
                  <Col md={10}>
                    <div className="d-flex">
                      <div className="flex-grow-1 me-1">
                        {" "}
                        {/* Small margin-end for minimal spacing */}
                        <AccountSearch
                          onAccountSelect={handleAccountSearchSelect}
                        />
                      </div>
                      <Button
                        className="d-flex align-content-center justify-content-center"
                        variant=""
                        size="lg"
                        onClick={() => {
                          setSearchNewAccount(false);
                          setSelectedAccount("");
                        }}
                      >
                        <i className="fa-solid fa-trash text-danger" />
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}

              <hr className="mt-0 mb-3" />

              <div className="step-content">{renderContent()}</div>
            </Col>
          </Row>
          
        </Modal.Body>

        <Modal.Footer>
          <div className="text-center">
            <Button
              variant="secondary"
              onClick={handleSave}
              className="text-white mt-1 w-100 d-flex align-items-center justify-content-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa-regular fa-floppy-disk"></i>
                  Submit
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}


export function useEditPatientModal() {
  const [show, setShow] = useState(false);
  const [pid, setPid] = useState(null);

  const open = (patientId) => {
    setPid(patientId || null);
    setShow(true);
  };
  const close = () => setShow(false);

  // Renderable modal element
  const Modal = () => (
    <PatientEditModal show={show} onHide={close} patientUpdateId={pid} />
  );

  return { open, Modal };
}

